import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './AgentFactorySuccess.styles';
import { AgentFactorySuccessProps } from './AgentFactorySuccess.types';
import { SliderImagesSection } from './components/SliderImagesSection';
import { Button, Link, Skeleton } from 'gatorcomponents';
import circlesImage from '@/media/onboarding/aiBuilder/circles-to-background.png';
import starImage from '@/media/onboarding/aiBuilder/star-to-background.png';
import themeV2 from '@/themes/defaultThemeV2';
import { agentFactoryPlansIds } from '@/pages/sites/SitesWizardHandler/SitesWizardHandler.enum';
import { sliderImagesContent } from './AgentFactorySlider';
import { AGENT_FACTORY_URL } from '@/config/urls/agentFactoryUrls';
import ConfettiAnimation from '@/media/factoryAgent/confetti.gif';
import { useResize } from '@/hooks/useResize';

import {
  AI_AGENTS_SITE_BUILDER_AGENT_FACTORY,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const AgentFactorySuccess = ({
  locales = {
    siteCreated: {
      title: 'Your site is ready!',
      button: {
        desktop: 'Go to editor',
        mobile: 'Edit site',
      },
    },
    agentBlock: {
      title: 'Create your AI Agent',
      subtitle: 'Enhance your website with a personalized AI Agent that interacts with your visitors, answers their questions, and provides support 24/7.',
      benefits: {
        customize: 'Customize the AI Agent to match your brand and style.',
        integrateBussiness: 'Integrate with your business tools for seamless operations.',
        integrateSite: 'Easily integrate the AI Agent into your website.',
        easyCreate: 'Easy to create and manage, no coding required.',
      },
      button: 'Create your AI Agent',
    },
  },
  handleRequestBuilderSso = () => {},
  loading,
  domainsList = [],
}) => {
  const showAgentFeatured = useFeatureIsOn(AI_AGENTS_SITE_BUILDER_AGENT_FACTORY);
  const isPlanEligible = agentFactoryPlansIds.some(planId => domainsList.includes(planId));
  const hasAgentCreated = false;
  const showAgentBlock = showAgentFeatured && isPlanEligible && !hasAgentCreated;

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 960;

  const [isLoading, setIsLoading] = useState(true);

  const checkLoading = useCallback(() => {
    if (domainsList.length > 0 && showAgentBlock) {
      setIsLoading(true);
    }
  }, [domainsList, showAgentBlock]);

  useEffect(() => {
    checkLoading();
  }, [checkLoading]);

  const LoadingSkeleton = () => (
    <Styles.SkeletonWrapper>
      <Skeleton width="100%" height="280px" />
      <Styles.SkeletonContent>
        <Styles.SkeletonBlock showAgentBlock={showAgentBlock}>
          <Skeleton width="218px" height="31px" />
          <Skeleton variant="button" width="175px" />
          <Skeleton width="100%" height="215px" />
        </Styles.SkeletonBlock>
        {showAgentBlock && (
          <Styles.SkeletonBlock showAgentBlock={showAgentBlock}>
            <Skeleton width="450px" height="67px" />
            <Skeleton width="450px" height="24px" />
            <Skeleton width="410px" height="285px" />
            <Skeleton variant="button" width="175px" />
          </Styles.SkeletonBlock>
        )}
      </Styles.SkeletonContent>
    </Styles.SkeletonWrapper>
  );

  if (!isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Styles.Wrapper data-testid="agent-factory-success-step">
      <Styles.WaveTop>
        <Styles.CirclesImage src={circlesImage} alt="loading-circles" />
        <Styles.StarImage src={starImage} alt="loading-star" />
      </Styles.WaveTop>

      <Styles.InnerContainer>
        <Styles.SiteCreatedBlock showAgentBlock={showAgentBlock}>
          <Styles.SiteCreatedTitle showAgentBlock={showAgentBlock}>{locales.siteCreated.title}</Styles.SiteCreatedTitle>
          <Styles.SiteCreatedButtonContainer>
            <Button
              variant="secondary"
              onClick={handleRequestBuilderSso}
              label={isMobile ? locales.siteCreated.button.mobile : locales.siteCreated.button.desktop}
              testId="go-to-ai-editor"
              size={isMobile ? 'medium2' : 'large'}
              loading={loading}
              iconRight={!loading && (
                <Styles.Svg
                  viewBox="0 0 21 20"
                  width={20}
                  height={20}
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  data-testid="agent-factory-pencil-icon"
                >
                  <path d="M15.5001 1.6665L18.8334 4.99984M2.16675 18.3332L3.23041 14.4331C3.29981 14.1786 3.33451 14.0514 3.38777 13.9328C3.43507 13.8274 3.49318 13.7273 3.56118 13.6339C3.63776 13.5288 3.73101 13.4356 3.9175 13.2491L12.5287 4.63791C12.6937 4.4729 12.7762 4.3904 12.8713 4.35949C12.955 4.3323 13.0452 4.3323 13.1288 4.35949C13.224 4.3904 13.3065 4.4729 13.4715 4.63791L15.862 7.02843C16.027 7.19344 16.1095 7.27595 16.1404 7.37108C16.1677 7.45476 16.1677 7.54491 16.1404 7.6286C16.1095 7.72373 16.027 7.80624 15.862 7.97125L7.25083 16.5824C7.06434 16.7689 6.97109 16.8622 6.86598 16.9388C6.77266 17.0068 6.67251 17.0648 6.56718 17.1122C6.44854 17.1654 6.32131 17.2001 6.06686 17.2695L2.16675 18.3332Z" stroke={themeV2.colors.brand.primaryMedium} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Styles.Svg>
              )}
            />
          </Styles.SiteCreatedButtonContainer>
          {(!isMobile || !showAgentBlock) && <SliderImagesSection content={sliderImagesContent} />}
        </Styles.SiteCreatedBlock>

        {showAgentBlock && (
          <>
            <Styles.DivisionBorder />
            <Styles.AgentBlock showAgentBlock={showAgentBlock}>
              <Styles.AgentBlockTitle>
                {locales.agentBlock.title}
              </Styles.AgentBlockTitle>
              <Styles.AgentBlockSubTitle>
                {locales.agentBlock.subtitle}
              </Styles.AgentBlockSubTitle>
              <Styles.AgentBlockBenefitsContainer>
                <Styles.AgentBlockBenefitsContent>
                  <Styles.AgentBlockBenefitsBox>
                    <Styles.Svg
                      viewBox="0 0 24 24"
                      width={24}
                      height={24}
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-testid="agent-factory-customizer-icon"
                    >
                      <path d="M3 7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V13.2C21 14.8802 21 15.7202 20.673 16.362C20.3854 16.9265 19.9265 17.3854 19.362 17.673C18.7202 18 17.8802 18 16.2 18H13.6837C13.0597 18 12.7477 18 12.4492 18.0613C12.1844 18.1156 11.9282 18.2055 11.6875 18.3285C11.4162 18.4671 11.1725 18.662 10.6852 19.0518L8.29976 20.9602C7.88367 21.2931 7.67563 21.4595 7.50054 21.4597C7.34827 21.4599 7.20422 21.3906 7.10923 21.2716C7 21.1348 7 20.8684 7 20.3355V18C6.07003 18 5.60504 18 5.22354 17.8978C4.18827 17.6204 3.37962 16.8117 3.10222 15.7765C3 15.395 3 14.93 3 14V7.8Z" stroke={themeV2.colors.brand.primaryLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.9969 7.69925C11.0972 6.6604 9.59689 6.38095 8.46961 7.33223C7.34234 8.2835 7.18364 9.87399 8.06889 10.9991C8.61733 11.6961 9.99389 12.9691 10.962 13.837C11.3176 14.1557 11.4953 14.3151 11.7087 14.3793C11.8918 14.4344 12.1021 14.4344 12.2851 14.3793C12.4985 14.3151 12.6763 14.1557 13.0319 13.837C13.9999 12.9691 15.3765 11.6961 15.9249 10.9991C16.8102 9.87399 16.6709 8.2735 15.5242 7.33223C14.3776 6.39096 12.8966 6.6604 11.9969 7.69925Z" stroke={themeV2.colors.brand.primaryLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Styles.Svg>
                    <Styles.AgentBlockBenefitsDescription>
                      {locales.agentBlock.benefits.customize}
                    </Styles.AgentBlockBenefitsDescription>
                  </Styles.AgentBlockBenefitsBox>

                  <Styles.AgentBlockBenefitsBox>
                    <Styles.Svg
                      viewBox="0 0 24 24"
                      width={24}
                      height={24}
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-testid="agent-factory-integrateBussiness-icon"
                    >
                      <path d="M12 20H5.2C4.07989 20 3.51984 20 3.09202 19.782C2.71569 19.5903 2.40973 19.2843 2.21799 18.908C2 18.4802 2 17.9201 2 16.8V7.2C2 6.07989 2 5.51984 2.21799 5.09202C2.40973 4.71569 2.71569 4.40973 3.09202 4.21799C3.51984 4 4.07989 4 5.2 4H5.6C7.84021 4 8.96031 4 9.81596 4.43597C10.5686 4.81947 11.1805 5.43139 11.564 6.18404C12 7.03968 12 8.15979 12 10.4M12 20V10.4M12 20H18.8C19.9201 20 20.4802 20 20.908 19.782C21.2843 19.5903 21.5903 19.2843 21.782 18.908C22 18.4802 22 17.9201 22 16.8V7.2C22 6.07989 22 5.51984 21.782 5.09202C21.5903 4.71569 21.2843 4.40973 20.908 4.21799C20.4802 4 19.9201 4 18.8 4H18.4C16.1598 4 15.0397 4 14.184 4.43597C13.4314 4.81947 12.8195 5.43139 12.436 6.18404C12 7.03968 12 8.15979 12 10.4" stroke={themeV2.colors.brand.primaryLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Styles.Svg>
                    <Styles.AgentBlockBenefitsDescription>
                      {locales.agentBlock.benefits.integrateBussiness}
                    </Styles.AgentBlockBenefitsDescription>
                  </Styles.AgentBlockBenefitsBox>

                  <Styles.AgentBlockBenefitsBox>
                    <Styles.Svg
                      viewBox="0 0 24 24"
                      width={24}
                      height={24}
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-testid="agent-factory-integrateSite-icon"
                    >
                      <path d="M3 16V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V16H15.6627C15.4182 16 15.2959 16 15.1808 16.0276C15.0787 16.0521 14.9812 16.0925 14.8917 16.1474C14.7908 16.2092 14.7043 16.2957 14.5314 16.4686L14.4686 16.5314C14.2957 16.7043 14.2092 16.7908 14.1083 16.8526C14.0188 16.9075 13.9213 16.9479 13.8192 16.9724C13.7041 17 13.5818 17 13.3373 17H10.6627C10.4182 17 10.2959 17 10.1808 16.9724C10.0787 16.9479 9.98119 16.9075 9.89172 16.8526C9.7908 16.7908 9.70432 16.7043 9.53137 16.5314L9.46863 16.4686C9.29568 16.2957 9.2092 16.2092 9.10828 16.1474C9.01881 16.0925 8.92127 16.0521 8.81923 16.0276C8.70414 16 8.58185 16 8.33726 16H3ZM3 16C2.44772 16 2 16.4477 2 17V17.3333C2 17.9533 2 18.2633 2.06815 18.5176C2.25308 19.2078 2.79218 19.7469 3.48236 19.9319C3.7367 20 4.04669 20 4.66667 20H19.3333C19.9533 20 20.2633 20 20.5176 19.9319C21.2078 19.7469 21.7469 19.2078 21.9319 18.5176C22 18.2633 22 17.9533 22 17.3333C22 17.0233 22 16.8683 21.9659 16.7412C21.8735 16.3961 21.6039 16.1265 21.2588 16.0341C21.1317 16 20.9767 16 20.6667 16H20" stroke={themeV2.colors.brand.primaryLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Styles.Svg>
                    <Styles.AgentBlockBenefitsDescription>
                      {locales.agentBlock.benefits.integrateSite}
                    </Styles.AgentBlockBenefitsDescription>
                  </Styles.AgentBlockBenefitsBox>

                  <Styles.AgentBlockBenefitsBox>
                    <Styles.Svg
                      viewBox="0 0 24 24"
                      width={24}
                      height={24}
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      data-testid="agent-factory-easyCreate-icon"
                    >
                      <path d="M22 9H2M6 17.5L8.5 15L6 12.5M11 17.5H15M2 7.8V16.2C2 17.8802 2 18.7202 2.32698 19.362C2.6146 19.9265 3.07354 20.3854 3.63803 20.673C4.27976 21 5.11984 21 6.8 21H17.2C18.8802 21 19.7202 21 20.362 20.673C20.9265 20.3854 21.3854 19.9265 21.673 19.362C22 18.7202 22 17.8802 22 16.2V7.8C22 6.11984 22 5.27977 21.673 4.63803C21.3854 4.07354 20.9265 3.6146 20.362 3.32698C19.7202 3 18.8802 3 17.2 3H6.8C5.11984 3 4.27976 3 3.63803 3.32698C3.07354 3.6146 2.6146 4.07354 2.32698 4.63803C2 5.27976 2 6.11984 2 7.8Z" stroke={themeV2.colors.brand.primaryLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </Styles.Svg>
                    <Styles.AgentBlockBenefitsDescription>
                      {locales.agentBlock.benefits.easyCreate}
                    </Styles.AgentBlockBenefitsDescription>
                  </Styles.AgentBlockBenefitsBox>
                </Styles.AgentBlockBenefitsContent>
              </Styles.AgentBlockBenefitsContainer>
              <Styles.AgentBlockButtonLink>
                <Link
                  href={AGENT_FACTORY_URL}
                  text={locales.agentBlock.button}
                  testId="access-agents-button"
                  target="_blank"
                />
              </Styles.AgentBlockButtonLink>
            </Styles.AgentBlock>
          </>
        )}
      </Styles.InnerContainer>
      <Styles.SuccessAnimation src={ConfettiAnimation} />
    </Styles.Wrapper>
  );
};

AgentFactorySuccess.propTypes = AgentFactorySuccessProps;

export default AgentFactorySuccess;
